import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Close, Hardware } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import { MobileWealthDTO } from '../../../ApiOld/Api/ApiServerVov';
import { DatePicker } from '@mui/x-date-pickers';
import LocalizationProviderWithLanguages from '../../Shared/Localization/LocalizationProviderWithLanguages';
import { useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';

export interface IMobileWealthDetailDialogProps {
    wealth: MobileWealthDTO | null;
    open: boolean;
    onClose: (confirmed: boolean, rentDateTo: Date | null) => void;
}

export function MobileWealthDetailDialog(props: IMobileWealthDetailDialogProps) {
    const { wealth, open, onClose } = props;
    const { t, i18n } = useTranslation();
    const [tempDate, setTempDate] = React.useState<Date | null>(null);
    // New boolean state: whether the user has changed the date.
    const [dateEdited, setDateEdited] = React.useState(false);
    const [showDatePicker, setShowDatePicker] = React.useState(false);

    // On mount/update, initialize tempDate from wealth.
    useEffect(() => {
        if (wealth) {
            setTempDate(wealth.rentDateTo ? new Date(wealth.rentDateTo) : new Date());
            setDateEdited(false);
        }
    }, [wealth]);

    if (!wealth) {
        return null;
    }

    // Determine which date to display:
    // If the user has edited the date, use tempDate;
    // otherwise, if wealth.rentDateTo exists, show that; else show tempDate.
    const displayDate = tempDate ? new Date(tempDate) : (wealth.rentDateTo ? new Date(wealth.rentDateTo) : null);

    return (
        <Modal
            open={open}
            onClose={() => onClose(false, null)}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
            }}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Stack
                display="flex"
                spacing={2}
                flexDirection="column"
                alignItems="center"
                sx={{ background: 'white', p: 2, borderRadius: 2, width: '90%', maxWidth: '600px' }}
            >
                <Stack direction="column" spacing={2} alignItems="center">
                    <img style={{ maxHeight: '400px', maxWidth: '100%' }} src={wealth.imagePreview} alt="wealth preview" />
                    <Stack direction="column" spacing={1} width="100%">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Hardware sx={{ color: '#FFC600' }} />
                            <Typography sx={{ fontSize: 14, fontWeight: 'bold', lineHeight: 1 }}>
                                {wealth.code} - {wealth.name}
                            </Typography>
                        </Stack>
                        {wealth.employeeCodeAndName ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonIcon sx={{ color: '#FFC600' }} />
                                <Typography
                                    sx={{
                                        lineHeight: 1,
                                        ...(wealth.borrowedByCurrentEmployee && { color: 'green' }),
                                    }}
                                >
                                    {wealth.employeeCodeAndName}
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckIcon sx={{ color: 'green' }} />
                                <Typography sx={{ color: 'green', lineHeight: 1 }}>{t('Available')}</Typography>
                            </Stack>
                        )}
                        {wealth.buildingCodeAndName && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <DomainOutlinedIcon sx={{ color: '#FFC600' }} />
                                <Typography sx={{ lineHeight: 1 }}>{wealth.buildingCodeAndName}</Typography>
                            </Stack>
                        )}
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <DateRangeIcon sx={{ color: '#FFC600' }} />
                            <Stack direction="column" spacing={1} pt={0.7}>
                                {wealth.rentDateFrom && (
                                    <Typography sx={{ fontSize: 15, lineHeight: 1 }}>
                                        {`${t('from')} ${new Date(wealth.rentDateFrom).toLocaleDateString(i18n.language)} `}
                                    </Typography>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ lineHeight: 1, color: displayDate && displayDate < new Date() ? 'red' : 'inherit' }}>
                                        {displayDate ? `${t('to').toLowerCase()} ${displayDate.toLocaleDateString(i18n.language)}` : ''}
                                    </Typography>
                                    <EditIcon onClick={() => setShowDatePicker(true)} />
                                </Stack>
                            </Stack>
                        </Stack>
                        <LocalizationProviderWithLanguages>
                            <DatePicker
                                open={showDatePicker}
                                value={tempDate}
                                onClose={() => setShowDatePicker(false)}
                                onChange={(date) => {
                                    setTempDate(date);
                                    setDateEdited(true);
                                    setShowDatePicker(false);
                                }}
                                renderInput={(params) => <Box style={{ display: 'none' }} />}
                            />
                        </LocalizationProviderWithLanguages>
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button
                                startIcon={<Close />}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    onClose(false, null);
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                endIcon={<Send />}
                                size="small"
                                variant="contained"
                                onClick={() => {
                                    onClose(true, tempDate);
                                }}
                            >
                                {t('Borrow')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Modal>
    );
}
